<template>
  <div class="delivery-edit-container" @click="closeTimePickerExcludeCheckbox">
    <div class="form-title">確認外送資訊</div>
    
    <!-- 日期選擇 -->
    <div class="form-field">
      <div class="w-full h-[56px] border border-[#AAAAAA] rounded-md">
        <el-date-picker 
          size="large" 
          v-model="selectedDate" 
          type="date" 
          placeholder="送達日期 *" 
          :disabled-date="disabledDate"
          :editable="false" 
          :clearable="false"
          @change="onDateChange">
        </el-date-picker>
      </div>
    </div>

    <!-- 時間選擇 -->
    <div class="form-field">
      <div class="w-full bg-white h-[56px] border border-[#AAAAAA] rounded-md relative"
        @click.stop="showTimePicker ? closeTimePicker($event) : displayTimePicker($event)">
        <div class="w-full h-full flex items-center justify-between px-3" v-if="!selectedTime">
          <div class="w-full h-full text-[22px] font-normal flex items-center" style="color: rgba(0, 0, 0, 0.3);">
            送達時間 *
          </div>
          <text class="button-icon size-7 text-[28px] self-center cursor-pointer"
            style="color: #FBCB02;">expand_more</text>
        </div>
        <div class="w-full h-full flex items-center justify-between text-[22px] font-normal px-3"
          style="color: #000;" v-else>
          {{ selectedTime }}
          <text class="button-icon size-7 text-[28px] self-center cursor-pointer"
            style="color: #FBCB02;">expand_more</text>
        </div>
        <transition name="time-picker-transition">
          <div class="w-[110%] left-[-5%] absolute top-[64px] bg-white grid grid-cols-4 py-4 px-2 rounded-md time-picker z-10"
            v-if="showTimePicker">
            <div class="flex items-center justify-center mx-1.5 px-4 h-11 border border-[#AAAAAA] rounded-md mb-2"
              v-for="(item, index) in timeOptions" :key="index" @click.stop="pickTime(item)">
              {{ item }}
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- 分隔線 -->
    <div class="divider"></div>

    <!-- 收件人資訊標題與儲存地址勾選框 -->
    <div class="row_center_between w-full px-2 mb-2">
      <text class="title">收件人資訊</text>
      <div class="checkbox-field">
        <el-checkbox 
          v-model="saveToAddresses" 
          size="large">
          {{ isNewAddress ? ' 設為預設地址' : ' 更新現有地址' }}
        </el-checkbox>
      </div>
    </div>

    <!-- 縣市/區域選擇 -->
    <div class="form-field">
      <div class="row_center_between w-full">
        <div class="area_select h-[56px] border border-[#AAAAAA] rounded-md" :class="{ 'has-value': isCitySelected }">
          <el-select v-model="selectedCity" size="large" placeholder="縣市 *" @change="onCityChange">
            <el-option value="縣市" disabled selected hidden>縣市</el-option>
            <el-option v-for="(city, index) in cities" :value="city.id" :key="index" :label="city.name">
            </el-option>
          </el-select>
        </div>
        
        <div class="area_select h-[56px] border border-[#AAAAAA] rounded-md" :class="{ 'has-value': isCityAreaSelected }">
          <el-select v-model="selectedCityArea" size="large" placeholder="區域 *" @change="onCityAreaChange">
            <el-option value="區域" disabled selected hidden>區域</el-option>
            <el-option v-for="(area, index) in matchedCityAreas" :value="area.id" :key="index" :label="area.name">
            </el-option>
          </el-select>
        </div>
      </div>
    </div>

    <!-- 地址輸入 -->
    <div class="form-field">
      <input 
        class="input" 
        placeholder="地址 *" 
        v-model="inputAddress" 
        @blur="onAddressChange"
      />
    </div>


    <!-- 聯絡人輸入 -->
    <div class="form-field">
      <input 
        class="input" 
        placeholder="聯絡人 *" 
        v-model="recipientName" 
        maxlength="20"
        @blur="onRecipientNameChange"
      />
    </div>

    <!-- 電話輸入 -->
    <div class="form-field">
      <input 
        inputmode="tel" 
        v-model="phone" 
        class="input" 
        placeholder="電話 *" 
        maxlength="15"
        @blur="onPhoneChange"
      />
    </div>

    <!-- 提示訊息 -->
    <div class="hint">
      <!-- 情況1: freeShipping > 0 且 shippingFee > 0，顯示完整訊息 -->
      <template v-if="freeShipping > 0 && shippingFee > 0">
        <p>消費滿{{ freeShipping }}元免費外送。</p>
        <p>未滿{{ freeShipping }}元，需加收運費{{ shippingFee }}元。</p>
      </template>
      
      <!-- 情況2: freeShipping = 0 但 shippingFee > 0，只顯示運費訊息 -->
      <p v-else-if="freeShipping === 0 && shippingFee > 0">需加收運費{{ shippingFee }}元。</p>
      
      <!-- 情況3: shippingFee = 0，不顯示任何訊息 -->
    </div>
  </div>
</template>

<script>
import commonUtilities from "@/utils/common.utils";
import areaData from "@/area_data";
import { dayjs } from 'element-plus';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'DeliveryEdit',
  data() {
    return {
      selectedDate: '',
      selectedTime: '',
      timeOptions: [],
      selectedCity: '',
      selectedCityArea: '',
      inputAddress: '',
      matchedCityAreas: [],
      phone: '',
      recipientName: '',
      showTimePicker: false,
      isCitySelected: false,
      isCityAreaSelected: false,
      saveToAddresses: false,      // 是否儲存到常用地址
      editingAddressId: null,      // 正在編輯的地址 ID
      isUpdatingAddress: false,    // 防止重複提交
      // 禁用日期邏輯（復用自 delivery.vue）
      disabledDate: (d) => {
        // 禁用今天之前的日期
        if (dayjs(d).isBefore(dayjs(), 'day')) {
          return true
        }
        
        // 禁用公休日（沒有營業時間的日期）
        const dayOfWeek = dayjs(d).day()
        const getterMap = {
          0: 'sundayBusinessHours',
          1: 'mondayBusinessHours',
          2: 'tuesdayBusinessHours',
          3: 'wednesdayBusinessHours',
          4: 'thursdayBusinessHours',
          5: 'fridayBusinessHours',
          6: 'saturdayBusinessHours'
        }
        
        const getterName = getterMap[dayOfWeek]
        const businessHours = this.$store.getters[`basic/${getterName}`] || []
        
        // 如果該日期沒有營業時間，則禁用
        return businessHours.length === 0
      }
    }
  },
  computed: {
    ...mapState('cart', {
      payment: 'payment'
    }),
    ...mapState('basic', {
      cities: 'cities',
      cityAreas: 'cityAreas',
    }),
    ...mapState('userData', {
      memberInfo: 'memberInfo'
    }),
    ...mapGetters('basic', {
      todayBusinessHours: 'todayBusinessHours',
      tomorrowBusinessHours: 'tomorrowBusinessHours',
      deliveryDelayHour: 'deliveryDelayHour',
      deliveryDelayMinute: 'deliveryDelayMinute',
      deliveryDelayTime: 'deliveryDelayTime',
      freeShipping: 'freeShipping',
      shippingFee: 'shippingFee'
    }),
    getBusinessHoursByDayOfWeek() {
      return (dayOfWeek) => {
        const getterMap = {
          0: 'sundayBusinessHours',
          1: 'mondayBusinessHours',
          2: 'tuesdayBusinessHours',
          3: 'wednesdayBusinessHours',
          4: 'thursdayBusinessHours',
          5: 'fridayBusinessHours',
          6: 'saturdayBusinessHours'
        }
        
        const getterName = getterMap[dayOfWeek]
        return this.$store.getters[`basic/${getterName}`] || []
      }
    },
    // 判斷是否為新增地址模式
    isNewAddress() {
      return !this.editingAddressId
    },
    // 取得當前會員的地址列表
    memberAddresses() {
      return this.memberInfo?.addresses ?? []
    },
    // 取得正在編輯的地址物件
    editingAddress() {
      if (!this.editingAddressId) return null
      return this.memberAddresses.find(addr => addr.id === this.editingAddressId) ?? null
    }
  },
  watch: {
    // 監聽 payment 變化，同步更新組件資料
    payment: {
      handler(newPayment, oldPayment) {
        // 只在特定情況下才重新初始化：
        // 1. addressId 改變（選擇了不同的常用地址）
        // 2. oldPayment 不存在（首次載入）
        
        if (!oldPayment || 
            newPayment.addressId !== oldPayment.addressId) {
          console.log('payment.addressId 改變，重新初始化')
          this.initializeData()
        } else {
          console.log('payment 變更但 addressId 未改變，跳過初始化')
        }
      },
      deep: true
    },
    selectedDate() {
      this.closeTimePicker()
      this.timeOptions = []
      this.setupTimeOptions()
    },
    selectedCity(newVal) {
      this.matchedCityAreas = []
      // 在 cityAreas 中找出與 selectedCity 相同的 city_id
      for (const cityArea of this.cityAreas) {
        if (cityArea.city_id == this.selectedCity) {
          this.matchedCityAreas.push(cityArea)
        }
      }

      const isCityAreaMatched = this.matchedCityAreas.some((cityArea) => {
        return cityArea.id == this.selectedCityArea
      })
      if (!isCityAreaMatched) {
        this.selectedCityArea = ''
      }
      
      // 更新選擇狀態
      this.isCitySelected = newVal !== '' && newVal !== '縣市'
    },
    selectedCityArea(newVal) {
      // 更新選擇狀態
      this.isCityAreaSelected = newVal !== '' && newVal !== '區域'
    }
  },
  async mounted() {
    // 初始化城市和區域資料
    if (!this.cities.length) await this.$store.dispatch('basic/fetchCities')
    if (!this.cityAreas.length) await this.$store.dispatch('basic/fetchCityAreas')
    
    // 初始化組件資料
    this.initializeData()
  },
  methods: {
    // 初始化組件資料
    initializeData() {
      console.log('=== initializeData 開始 ===')
      console.log('payment:', this.payment)
      console.log('memberInfo:', this.memberInfo)
      console.log('memberInfo.addresses:', this.memberInfo?.addresses)
      
      // 從 payment 讀取 addressId
      this.editingAddressId = this.payment.addressId ?? null
      console.log('editingAddressId:', this.editingAddressId)
      
      // 處理日期：將字串轉換為 Date 物件
      if (this.payment.pickupDate) {
        const dateStr = this.payment.pickupDate
        const dateParts = dateStr.split('/')
        if (dateParts.length === 3) {
          this.selectedDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
        }
      } else {
        this.selectedDate = ''
      }
      
      // 處理時間
      this.selectedTime = this.payment.pickupTime || ''
      
      // 處理電話
      this.phone = this.payment.phone || ''
      
      // 處理聯絡人
      this.recipientName = this.payment.recipientName || ''
      
      // 處理地址：需要解析完整地址
      if (this.payment.address) {
        this.parseAddress(this.payment.address)
      }
      
      // 如果有日期，設定時間選項
      if (this.selectedDate) {
        this.setupTimeOptions()
      }
      
      // 如果沒有地址，自動勾選（但不立即儲存）
      if (this.memberAddresses.length === 0) {
        this.saveToAddresses = true
        console.log('→ 自動勾選 saveToAddresses（沒有地址）')
      } else {
        this.saveToAddresses = false
        console.log('→ 不勾選 saveToAddresses（有地址）')
      }
      
      console.log('=== initializeData 結束 ===')
    },
    
    // 解析完整地址
    parseAddress(fullAddress) {
      // 嘗試從完整地址中解析出縣市、區域和詳細地址
      for (const city of this.cities) {
        if (fullAddress.startsWith(city.name)) {
          this.selectedCity = city.id
          const remainingAddress = fullAddress.substring(city.name.length)
          
          // 找出匹配的區域
          for (const cityArea of this.cityAreas) {
            if (cityArea.city_id == city.id && remainingAddress.startsWith(cityArea.name)) {
              this.selectedCityArea = cityArea.id
              this.inputAddress = remainingAddress.substring(cityArea.name.length)
              return
            }
          }
        }
      }
      
      // 如果無法解析，將整個地址放入 inputAddress
      this.inputAddress = fullAddress
    },
    
    // 日期變更處理
    onDateChange() {
      this.updatePaymentDate()
      this.setupTimeOptions()
    },
    
    // 時間選擇處理
    pickTime(time) {
      this.selectedTime = time
      this.closeTimePicker()
      this.updatePaymentTime()
    },
    
    // 縣市變更處理
    onCityChange() {
      this.updatePaymentAddress()
    },
    
    // 區域變更處理
    onCityAreaChange() {
      this.updatePaymentAddress()
    },
    
    // 地址變更處理
    onAddressChange() {
      this.updatePaymentAddress()
    },
    
    // 關閉時間選擇器（排除 checkbox 區域）
    closeTimePickerExcludeCheckbox(event) {
      // 如果點擊的是 checkbox 區域，不關閉時間選擇器
      if (event.target.closest('.checkbox-field')) {
        console.log('點擊 checkbox 區域，不關閉時間選擇器')
        return
      }
      this.closeTimePicker(event)
    },
    
    // 驗證所有必填欄位（除了統編）
    validateRequiredFields() {
      const errors = []
      
      if (!this.selectedDate) {
        errors.push('請選擇送達日期')
      }
      
      if (!this.selectedTime) {
        errors.push('請選擇送達時間')
      }
      
      if (!this.selectedCity) {
        errors.push('請選擇縣市')
      }
      
      if (!this.selectedCityArea) {
        errors.push('請選擇區域')
      }
      
      if (!this.inputAddress || this.inputAddress.trim() === '') {
        errors.push('請輸入地址')
      }
      
      if (!this.recipientName || this.recipientName.trim() === '') {
        errors.push('請輸入聯絡人')
      }
      
      if (!this.phone || this.phone.trim() === '') {
        errors.push('請輸入電話')
      }
      
      // 驗證電話格式
      if (this.phone && !this.validatePhoneNumber(this.phone)) {
        errors.push('請輸入正確的電話號碼格式')
      }
      
      return errors
    },
    
    // 電話驗證
    validatePhoneNumber(phone) {
      const cleanPhone = phone.replace(/[\s-]/g, '')
      
      // 手機：09 開頭 10 碼
      const mobilePattern = /^09\d{8}$/
      if (mobilePattern.test(cleanPhone)) {
        return true
      }
      
      // 市話：區碼 + 號碼
      const landlinePatterns = [
        /^02\d{7,8}$/,        // 台北市
        /^0[3-8]\d{7}$/,      // 其他縣市 7 碼
        /^0[3-8]\d{6}$/,      // 其他縣市 6 碼
        /^0[3-8]{2}\d{5,6}$/  // 三碼區碼
      ]
      
      for (const pattern of landlinePatterns) {
        if (pattern.test(cleanPhone)) {
          return true
        }
      }
      
      return false
    },
    
    // 如果 checkbox 已勾選，則儲存地址（供父組件呼叫）
    async saveIfChecked() {
      // 如果 checkbox 未勾選，直接返回
      if (!this.saveToAddresses) {
        return
      }
      
      // 驗證必填欄位
      const errors = this.validateRequiredFields()
      if (errors.length > 0) {
        throw new Error(errors[0])
      }
      
      // 儲存地址
      await this.saveAddressToMemberInfo()
    },
    
    // 驗證所有必填欄位（除了統編）
    async saveAddressToMemberInfo() {
      if (this.isUpdatingAddress) return
      
      try {
        this.isUpdatingAddress = true
        
        // 驗證必填欄位
        const errors = this.validateRequiredFields()
        if (errors.length > 0) {
          commonUtilities.createToast('error', errors[0])
          this.saveToAddresses = false
          return
        }
        
        const city = this.cities.find((city) => city.id == this.selectedCity)
        const cityArea = this.cityAreas.find((cityArea) => cityArea.id == this.selectedCityArea)
        
        if (!city || !cityArea) {
          throw new Error('無法取得縣市或區域資料')
        }
        
        // 從 areaData 取得正確的 postcode
        const postcode = areaData[this.selectedCityArea] ?? ''
        
        // 取得當前會員資料
        const currentMemberInfo = this.memberInfo
        if (!currentMemberInfo) {
          throw new Error('無法取得會員資料')
        }
        
        const currentAddresses = currentMemberInfo.addresses ?? []
        let updatedAddresses = []
        
        // 判斷是新增還是更新
        if (currentAddresses.length === 0) {
          // 情況 1: addresses 為空，新增第一個地址並設為預設
          updatedAddresses = [{
            address: this.inputAddress,
            city_id: this.selectedCity,
            cityarea_id: this.selectedCityArea,
            name: this.recipientName,
            phone: this.phone,
            postcode: postcode,
            is_default: 1,  // 設為預設地址
            type: 1,        // 第一個地址 type=1
            comment: null
          }]
          
          commonUtilities.createToast('success', '地址已儲存為預設地址')
        } else {
          // 情況 2: addresses 不為空，更新現有地址
          
          if (this.editingAddressId) {
            // 有 addressId，更新該地址
            const addressIndex = currentAddresses.findIndex(addr => addr.id === this.editingAddressId)
            
            if (addressIndex !== -1) {
              updatedAddresses = [...currentAddresses]
              updatedAddresses[addressIndex] = {
                ...updatedAddresses[addressIndex],
                address: this.inputAddress,
                city_id: this.selectedCity,
                cityarea_id: this.selectedCityArea,
                name: this.recipientName,
                phone: this.phone,
                postcode: postcode,
                comment: null
                // 保留原有的 id, address_id, is_default, type
              }
              
              commonUtilities.createToast('success', '地址已更新')
            } else {
              throw new Error('找不到要更新的地址')
            }
          } else {
            // 沒有 addressId，表示使用者手動輸入新地址（不應該發生，但作為保護）
            throw new Error('無法識別要更新的地址，請從常用地址選擇')
          }
        }
        
        // 準備完整的會員資料
        const updatedMemberInfo = {
          ...currentMemberInfo,
          addresses: updatedAddresses
        }
        
        // 呼叫 updateMemberInfo
        await this.$store.dispatch('userData/updateMemberInfo', updatedMemberInfo)
        
      } catch (error) {
        console.error('儲存地址失敗:', error)
        commonUtilities.createToast('error', error.message || '儲存地址失敗')
        this.saveToAddresses = false
      } finally {
        this.isUpdatingAddress = false
      }
    },
    
    // 聯絡人變更處理
    onRecipientNameChange() {
      const payment = { ...this.payment }
      payment.recipientName = this.recipientName
      this.$store.commit("cart/setPayment", payment)
    },
    
    // 電話變更處理
    onPhoneChange() {
      // 如果有輸入電話，進行驗證
      if (this.phone && this.phone.trim() !== "") {
        if (!this.validatePhoneNumber(this.phone)) {
          commonUtilities.createToast("error", "請輸入正確的電話號碼格式（手機：09開頭10碼，或市話含區碼）");
          // 清空錯誤的電話
          this.phone = '';
          return;
        }
      }
      
      // 驗證通過或為空，更新到 payment
      const payment = { ...this.payment };
      payment.phone = this.phone;
      this.$store.commit("cart/setPayment", payment);
    },
    
    // 更新付款資訊中的日期
    updatePaymentDate() {
      if (!this.selectedDate) return
      
      const payment = { ...this.payment }
      payment.pickupDate = commonUtilities.convertDate(this.selectedDate)
      this.$store.commit("cart/setPayment", payment)
    },
    
    // 更新付款資訊中的時間
    updatePaymentTime() {
      const payment = { ...this.payment }
      payment.pickupTime = this.selectedTime
      this.$store.commit("cart/setPayment", payment)
    },
    
    // 更新付款資訊中的地址
    updatePaymentAddress() {
      if (!this.selectedCity || !this.selectedCityArea || !this.inputAddress) return
      
      const city = this.cities.find((city) => city.id == this.selectedCity)
      const cityArea = this.cityAreas.find((cityArea) => cityArea.id == this.selectedCityArea)
      
      if (!city || !cityArea) return
      
      const payment = { ...this.payment }
      payment.address = city.name + cityArea.name + this.inputAddress
      this.$store.commit("cart/setPayment", payment)
    },
    
    // 時間選擇相關方法（復用自 delivery.vue）
    setupTimeOptions() {
      if (!this.selectedDate) {
        return
      }
      
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const selectedDateStr = commonUtilities.convertDate(this.selectedDate)
      const todayStr = commonUtilities.convertDate(today)
      const tomorrowStr = commonUtilities.convertDate(tomorrow)
      
      this.timeOptions = []
      
      if (selectedDateStr === todayStr) {
        // 今天
        if (this.todayBusinessHours && this.todayBusinessHours.length > 0) {
          for (const item of this.todayBusinessHours) {
            this.timeOptionHelper({...item}, true)
          }
        }
      } else if (selectedDateStr === tomorrowStr) {
        // 明天
        if (this.tomorrowBusinessHours && this.tomorrowBusinessHours.length > 0) {
          for (const item of this.tomorrowBusinessHours) {
            this.timeOptionHelper({...item}, false)
          }
        }
      } else {
        // 未來日期
        const selectedDayOfWeek = dayjs(this.selectedDate).day()
        const businessHours = this.getBusinessHoursByDayOfWeek(selectedDayOfWeek)
        
        if (businessHours && businessHours.length > 0) {
          for (const item of businessHours) {
            this.timeOptionHelper({...item}, false)
          }
        }
      }
      
      this.timeOptions = this.timeOptions.filter((item, index, self) => self.indexOf(item) === index)
    },
    
    timeOptionHelper(item, isToday = false) {
      const today = new Date()
      
      // 只有在選擇今天時,才需要考慮當前時間
      if (isToday) {
        const currentHourLargerThanStartHour = Number(item.startTime.split(':')[0]) < today.getHours()
        const currentMinuteLargerThanStartMinute = Number(item.startTime.split(':')[1]) < today.getMinutes()
        const currentHourEqualToStartHour = Number(item.startTime.split(':')[0]) === today.getHours()
        if (currentHourLargerThanStartHour || (currentHourEqualToStartHour && currentMinuteLargerThanStartMinute)) {
          item.startTime = `${today.getHours()}:${today.getMinutes()}`
        }
      }
      
      // 加入店家設定等候時間
      let startHour = parseInt(item.startTime.split(':')[0]) + this.deliveryDelayHour;
      let startMinute = parseInt(item.startTime.split(':')[1]) + this.deliveryDelayMinute;
      
      // 處理分鐘進位
      if (startMinute >= 60) {
        startHour += 1;
        startMinute -= 60;
      }

      const endHour = parseInt(item.endTime.split(':')[0])
      const endMinute = parseInt(item.endTime.split(':')[1])

      for (let hour = startHour; hour <= endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          if (hour === startHour && minute < startMinute) {
            continue
          }
          if (hour === endHour && minute > endMinute) {
            break
          }
          let time = `${hour < 10 ? '0' + hour : hour}:${minute < 10 ? '0' + minute : minute}`
          this.timeOptions.push(time)
        }
      }
    },
    
    displayTimePicker(event) {
      if (!this.selectedDate) {
        commonUtilities.createToast('info', '請先選擇日期')
        return
      }
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      this.showTimePicker = true
    },
    
    closeTimePicker(event) {
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      this.showTimePicker = false
    },
    
    // 供外部呼叫的驗證方法（驗證所有必填欄位）
    validateAllFields() {
      // 驗證送達日期
      if (!this.selectedDate) {
        commonUtilities.createToast("error", "請選擇送達日期")
        return false
      }
      
      // 驗證送達時間
      if (!this.selectedTime) {
        commonUtilities.createToast("error", "請選擇送達時間")
        return false
      }
      
      // 驗證縣市
      if (!this.selectedCity || this.selectedCity === '') {
        commonUtilities.createToast("error", "請選擇縣市")
        return false
      }
      
      // 驗證區域
      if (!this.selectedCityArea || this.selectedCityArea === '') {
        commonUtilities.createToast("error", "請選擇區域")
        return false
      }
      
      // 驗證地址
      if (!this.inputAddress || this.inputAddress.trim() === '') {
        commonUtilities.createToast("error", "請輸入地址")
        return false
      }
      
      // 驗證聯絡人
      if (!this.recipientName || this.recipientName.trim() === '') {
        commonUtilities.createToast("error", "請輸入聯絡人")
        return false
      }
      
      // 驗證電話
      if (!this.phone || this.phone.trim() === '') {
        commonUtilities.createToast("error", "請輸入電話")
        return false
      }
      
      // 驗證電話格式
      if (this.phone && !this.validatePhoneNumber(this.phone)) {
        commonUtilities.createToast("error", "請輸入正確的電話號碼格式")
        return false
      }
      
      // 所有驗證通過
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.delivery-edit-container {
  width: 100%;
  padding: 0;
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  text-align: center;
}

.form-field {
  margin-bottom: 12px;
}

// 輸入框樣式（復用自 delivery.vue）
.input {
  font-family: PingFang SC;
  width: 100%;
  padding: 0 4%;
  height: 54px;
  border: 1px solid #AAAAAA;
  border-radius: 6px;
  font-size: 22px;
  text-align: left;
  color: #000;
  font-weight: 400;
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.3);
    font-weight: 400;
  }
}

// 地址選擇器樣式
.area_select {
  width: 47%;
}

.row_center_between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

// 提示訊息
.hint {
  width: 100%;
  line-height: 20px;
  font-size: 14px;
  font-family: PingFang SC;
  color: #fd5001;
  margin-top: 12px;
}

// 勾選框樣式
.checkbox-field {
  position: relative;
  z-index: 10;
  pointer-events: auto;
  
  :deep(.el-checkbox) {
    pointer-events: auto;
    
    .el-checkbox__label {
      font-size: 16px;
      color: #333;
      pointer-events: auto;
    }
    
    .el-checkbox__input {
      pointer-events: auto;
    }
  }
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.divider {
  width: 100%;
  height: 1px;
  background-color: #DDDDDD;
  margin: 16px 0;
}

// 時間選擇器動畫（復用自 delivery.vue）
.time-picker-transition-enter-active,
.time-picker-transition-leave-active {
  transition: 0.2s ease-in-out;
}

.time-picker-transition-enter-from,
.time-picker-transition-leave-to {
  max-height: 0;
  opacity: 0;
  transform: scaleY(0);
}

.time-picker-transition-enter-to,
.time-picker-transition-leave-from {
  opacity: 1;
  transform: scaleY(1);
}

.time-picker {
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  -webkit-box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  -moz-box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  z-index: 1000;
}

// Element Plus 日期選擇器樣式調整（復用自 delivery.vue）
:deep(.el-date-editor) {
  width: 100%;
  position: relative;
  
  .el-input__prefix {
    position: absolute;
    right: 4px;
    left: auto;
    z-index: 1;
    
    .el-input__prefix-inner {
      .el-icon {
        color: #FBCB02;
        font-size: 28px;
        width: 28px;
        height: 28px;
      }
    }
  }
  
  .el-input__inner {
    padding-left: 2px;
    padding-right: 40px;
    color: #000;
  }
  
  .el-input__inner::placeholder {
    color: rgba(0, 0, 0, 0.3);
  }
}

// Element Plus 下拉選單樣式（復用自 delivery.vue）
.area_select {
  :deep(.el-select) {
    width: 100%;
  }
  
  :deep(.el-select__placeholder) {
    color: rgba(0, 0, 0, 0.3) !important;
  }
  
  &.has-value {
    :deep(.el-select__selected-item) {
      color: #000 !important;
    }
    
    :deep(input) {
      color: #000 !important;
    }
    
    :deep(.el-input__inner) {
      color: #000 !important;
    }
  }
}
</style>
